import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Box, Input, Icon } from "@cloudscape-design/components";
import { useChatSession } from "../../context/ChatSessionContext";
import { useMenu } from "../../context/MenuContext";
import { useHistory } from "react-router-dom";

export default function CommandPalette() {
  const [open, setOpen]                 = useState(false);
  const [query, setQuery]               = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef                         = useRef(null);

  const {
    sessions, savedPrompts,
    startNewChat, openSession,
    setQueryMode, setPendingPromptText,
  } = useChatSession();
  const { visibleMenus } = useMenu();
  const history = useHistory();

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  // Build flat items list filtered by query
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [
      {
        group: "Actions",
        label: "New Chat",
        icon: "add-plus",
        action: () => { startNewChat(); history.push("/boxer/execute-query"); },
      },
      ...visibleMenus.map(m => ({
        group: "Pages",
        label: m.name,
        icon: m.icon || "external",
        action: () => history.push(m.path),
      })),
      ...sessions.slice(0, 20).map(s => ({
        group: "Recent Chats",
        label: s.title || "Untitled",
        icon: "contact",
        action: () => { openSession(s); history.push("/boxer/execute-query"); },
      })),
      ...savedPrompts.map(p => ({
        group: "Saved Prompts",
        label: p.title,
        icon: "script",
        action: () => {
          startNewChat();
          setQueryMode(p.query_mode || "schema");
          setPendingPromptText(p.content);
          history.push("/boxer/execute-query");
        },
      })),
    ];
    if (!q) return all;
    return all.filter(item => item.label.toLowerCase().includes(q));
  }, [query, sessions, savedPrompts, visibleMenus, startNewChat, openSession, history, setQueryMode, setPendingPromptText]);

  // Reset selection when results change
  useEffect(() => { setSelectedIndex(0); }, [items]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${selectedIndex}"]`);
      if (el) el.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const executeSelected = useCallback(() => {
    const item = items[selectedIndex];
    if (item) { item.action(); handleClose(); }
  }, [items, selectedIndex, handleClose]);

  const handleKeyDown = useCallback(({ detail }) => {
    if (detail.key === "ArrowDown") setSelectedIndex(i => Math.min(i + 1, items.length - 1));
    if (detail.key === "ArrowUp")   setSelectedIndex(i => Math.max(i - 1, 0));
    if (detail.key === "Enter")     executeSelected();
    if (detail.key === "Escape")    handleClose();
  }, [items.length, executeSelected, handleClose]);

  if (!open) return null;

  // Build display elements with group headers
  let lastGroup = null;
  const displayElements = [];
  items.forEach((item, idx) => {
    if (item.group !== lastGroup) {
      displayElements.push(
        <div key={`hdr-${item.group}`} style={{
          padding: "8px 16px 4px",
          fontSize: 11,
          fontWeight: 600,
          fontFamily: "inherit",
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          userSelect: "none",
        }}>
          {item.group}
        </div>
      );
      lastGroup = item.group;
    }

    const isSelected = idx === selectedIndex;
    displayElements.push(
      <div
        key={idx}
        data-idx={idx}
        onClick={() => { item.action(); handleClose(); }}
        onMouseEnter={() => setSelectedIndex(idx)}
        style={{
          padding: "8px 16px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: isSelected ? "#f0f4ff" : "transparent",
          borderLeft: isSelected ? "3px solid #0972d3" : "3px solid transparent",
          transition: "background 0.1s",
        }}
      >
        <span style={{ color: isSelected ? "#0972d3" : "#9ca3af", flexShrink: 0, display: "flex" }}>
          <Icon name={item.icon} size="small" />
        </span>
        <Box fontSize="body-m" color={isSelected ? "text-status-info" : "text-label"}>
          {item.label}
        </Box>
      </div>
    );
  });

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: 80,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          width: "100%", maxWidth: 560,
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          overflow: "hidden",
          fontFamily: "'Open Sans', 'Helvetica Neue', Roboto, Arial, sans-serif",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e3e8ef" }}>
          <Input
            autoFocus
            placeholder="Search chats, prompts, pages..."
            value={query}
            onChange={({ detail }) => setQuery(detail.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: 380, overflowY: "auto" }}>
          {items.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Box color="text-body-secondary">No results for "{query}"</Box>
            </div>
          ) : displayElements}
        </div>

        {/* Keyboard hints */}
        <div style={{
          padding: "6px 16px",
          borderTop: "1px solid #e3e8ef",
          display: "flex", gap: 20,
          background: "#f8f9fa",
          fontFamily: "inherit",
        }}>
          <Box color="text-body-secondary" fontSize="body-s">↑↓ navigate</Box>
          <Box color="text-body-secondary" fontSize="body-s">↵ open</Box>
          <Box color="text-body-secondary" fontSize="body-s">Esc close</Box>
        </div>
      </div>
    </div>
  );
}
